import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import type { Destination } from '../domain/model';
import { createDemoSource } from './demoSource';
import { createSupabaseSource } from './supabaseSource';
import type { AppConfig, DataSource, Session } from './source';

const DEMO_FLAG = 'elio-demo';

function storage(kind: 'local' | 'session') {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

export function demoRequested(): boolean {
  return import.meta.env.VITE_DEMO === '1' || storage('session')?.getItem(DEMO_FLAG) === '1';
}

export function setDemo(on: boolean) {
  const s = storage('session');
  if (on) s?.setItem(DEMO_FLAG, '1');
  else s?.removeItem(DEMO_FLAG);
  window.location.assign('/');
}

function chooseSource(): DataSource {
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
  if (demoRequested() || !url || !key) {
    return createDemoSource(async () => (await import('./catalog.demo.json')).default as Destination[]);
  }
  return createSupabaseSource(url, key);
}

export type Scope = { kind: 'personal' } | { kind: 'group'; groupId: string; name: string };

interface AppCtx {
  data: DataSource;
  session: Session | null;
  sessionLoading: boolean;
  scope: Scope;
  setScope: (s: Scope) => void;
  activeTripOverride: string | null;
  setActiveTripOverride: (id: string | null) => void;
  config: AppConfig | undefined;
  announce: (msg: string) => void;
  backendConfigured: boolean;
}

const Ctx = createContext<AppCtx | null>(null);
const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 15_000, retry: 1, refetchOnWindowFocus: true } } });

function readJson<T>(key: string, fallback: T): T {
  try {
    const v = storage('local')?.getItem(key);
    return v ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
}
function writeJson(key: string, v: unknown) {
  try {
    storage('local')?.setItem(key, JSON.stringify(v));
  } catch {
    /* storage unavailable: preference lasts for this visit only */
  }
}

export function AppProvider({ children }: { children: ReactNode }) {
  const data = useMemo(chooseSource, []);
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [scope, setScopeState] = useState<Scope>(() => readJson<Scope>('elio-scope', { kind: 'personal' }));
  const [activeTripOverride, setOverride] = useState<string | null>(() => readJson<string | null>('elio-active-trip', null));
  const [message, setMessage] = useState('');

  useEffect(() => {
    let alive = true;
    data.getSession().then((s) => {
      if (alive) { setSession(s); setSessionLoading(false); }
    });
    const off = data.onAuthChange((s) => {
      setSession((prev) => {
        // Account switch or sign-out: drop every cached query so no other user's data is shown.
        if (prev?.userId !== s?.userId) queryClient.clear();
        return s;
      });
      if (!s) {
        try { storage('local')?.removeItem('elio-scope'); storage('local')?.removeItem('elio-active-trip'); } catch { /* ignore */ }
      }
    });
    return () => { alive = false; off(); };
  }, [data]);

  const configQuery = useQuery({ queryKey: ['config'], queryFn: () => data.getConfig(), staleTime: 5 * 60_000 }, queryClient);

  const setScope = useCallback((s: Scope) => { setScopeState(s); writeJson('elio-scope', s); }, []);
  const setActiveTripOverride = useCallback((id: string | null) => { setOverride(id); writeJson('elio-active-trip', id); }, []);
  const announce = useCallback((m: string) => { setMessage(''); window.setTimeout(() => setMessage(m), 30); }, []);

  const value: AppCtx = {
    data, session, sessionLoading, scope, setScope, activeTripOverride, setActiveTripOverride, config: configQuery.data, announce,
    backendConfigured: Boolean(import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY),
  };
  return (
    <QueryClientProvider client={queryClient}>
      <Ctx.Provider value={value}>
        {children}
        <div aria-live="polite" role="status" className="sr-only">{message}</div>
      </Ctx.Provider>
    </QueryClientProvider>
  );
}

export function useApp(): AppCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useApp outside provider');
  return c;
}

export function useCapability(name: string) {
  const { config } = useApp();
  return config?.capabilities.find((c) => c.capability === name);
}

export { queryClient };
